import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { roomApi, roomSchemas } from "@/api/client/room"
import { getErrorMessage } from "@/api/instance"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { RoomForm, type RoomFormValues } from "@/components/properties/RoomForm"
import { RoomHeader } from "@/components/properties/RoomHeader"
import { Button } from "@/components/ui/button/button"
import { Loading } from "@/components/ui/loading"

export function RoomEditPage() {
  const navigate = useNavigate()
  const { propertyId: propertyIdParam, roomId: roomIdParam } = useParams()
  const parsedPropertyId = Number(propertyIdParam)
  const propertyId = Number.isFinite(parsedPropertyId) ? parsedPropertyId : undefined
  const parsedRoomId = Number(roomIdParam)
  const roomId = Number.isFinite(parsedRoomId) ? parsedRoomId : undefined
  const hasValidIds = propertyId !== undefined && roomId !== undefined

  const {
    data: roomResponse,
    isLoading: isRoomLoading,
    error: roomError,
    refetch,
  } = roomApi.useRoomGet(roomId, undefined, {
    query: {
      enabled: hasValidIds,
    },
  })

  const roomUpdateMutation = roomApi.useRoomUpdate({
    mutation: {
      onSuccess: async () => {
        toast.success("更新しました。")
        await refetch()
        if (propertyId !== undefined && roomId !== undefined) {
          navigate(`/properties/${propertyId}/rooms/${roomId}`, { state: { shouldRefetch: true } })
        } else {
          navigate("/properties")
        }
      },
      onError: (error) => {
        toast.error("更新に失敗しました。", { description: getErrorMessage(error) })
      },
    },
  })

  const handleSubmit = async (values: RoomFormValues) => {
    if (propertyId === undefined || roomId === undefined) {
      toast.error("物件情報または部屋情報が見つかりません。")
      return
    }

    try {
      const normalizedFloor = Number(values.floor) || 0
      const data = roomSchemas.roomUpdateBody.parse({
        propertyId,
        name: values.name,
        layout: values.layout ?? "",
        size: values.size ?? "",
        floor: Number.isFinite(normalizedFloor) ? normalizedFloor : 0,
      })

      await roomUpdateMutation.mutateAsync({ id: roomId, data })
    } catch (error) {
      console.error("Error submitting room:", error)
    }
  }

  const handleCancel = () => {
    if (propertyId !== undefined && roomId !== undefined) {
      navigate(`/properties/${propertyId}/rooms/${roomId}`)
    } else if (propertyId !== undefined) {
      navigate(`/properties/${propertyId}`, { state: { shouldRefetch: true } })
    } else {
      navigate("/properties")
    }
  }

  return (
    <>
      {isRoomLoading ? <Loading className="mt-10" text="部屋情報を読み込んでいます..." /> : null}

      {!isRoomLoading && (!hasValidIds || roomError || !roomResponse) ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4">
          <div className="text-slate-600">
            {hasValidIds ? "部屋情報の取得に失敗しました。" : "部屋情報が見つかりませんでした。"}
          </div>
          <Button onClick={() => navigate(-1)} variant="outline">
            前のページに戻る
          </Button>
        </div>
      ) : null}

      {!isRoomLoading && roomResponse && propertyId && roomId ? (
        <div className="space-y-5">
          <PropertyHeader propertyId={propertyId} />
          <RoomHeader roomId={roomId} />

          <RoomForm
            defaultValues={{
              name: roomResponse.room.name,
              layout: roomResponse.room.layout ?? "",
              size: roomResponse.room.size ?? "",
              floor: roomResponse.room.floor ? String(roomResponse.room.floor) : "",
            }}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={roomUpdateMutation.isPending}
            isEdit
          />
        </div>
      ) : null}
    </>
  )
}
